/**
 * Interactive Intent Extractor
 *
 * Extracts interactive elements from a UI tree and infers their intents
 * from element text/context. Reusable by both the interactive fixer and
 * external consumers (e.g., generation pipeline for pre-generation hints).
 *
 * Extracted from apply-interactive-fixes.ts to eliminate duplication.
 */

// ─── Intent Mapping Table ────────────────────────────────────────

export interface IntentDef {
  /** Regex patterns to match against element text (case-insensitive) */
  patterns: RegExp[]
  /** Flow name to generate */
  flowName: string
  /** Steps to insert into the flow */
  steps: Record<string, unknown>[]
}

export const INTENT_MAP: IntentDef[] = [
  {
    patterns: [/add\s*to\s*cart/i, /add\s*to\s*bag/i, /add\s*item/i],
    flowName: "addToCart",
    steps: [
      {
        id: "s1", type: "action", action: "setState",
        params: { target: "appData.cart.items", operation: "append", value: "#{event.item}" },
      },
    ],
  },
  {
    patterns: [/buy\s*now/i, /checkout/i, /proceed\s*to\s*(checkout|payment)/i],
    flowName: "buyNow",
    steps: [{ id: "s1", type: "action", action: "navigate", params: { route: "/checkout" } }],
  },
  {
    patterns: [/remove/i, /delete\s*from\s*cart/i, /remove\s*item/i],
    flowName: "removeItem",
    steps: [
      {
        id: "s1", type: "action", action: "setState",
        params: { target: "appData.cart.items", operation: "removeWhere", condition: "#{event.itemId}" },
      },
    ],
  },
  {
    patterns: [/submit/i, /send/i, /post/i],
    flowName: "submitForm",
    steps: [
      {
        id: "s1", type: "action", action: "restApi",
        params: { method: "POST", url: "#{appData.apiBase}/submit", body: "#{event.formData}" },
        resultTarget: "_results.submitResult",
      },
    ],
  },
  {
    patterns: [/search/i, /find/i, /filter/i],
    flowName: "searchItems",
    steps: [
      {
        id: "s1", type: "action", action: "setState",
        params: { target: "appData.searchQuery", operation: "set", value: "#{event.value}" },
      },
    ],
  },
  {
    patterns: [/log\s*in/i, /sign\s*in/i],
    flowName: "login",
    steps: [
      {
        id: "s1", type: "action", action: "restApi",
        params: { method: "POST", url: "#{appData.apiBase}/auth/login", body: "#{event.credentials}" },
        resultTarget: "_results.loginResult",
      },
      {
        id: "s2", type: "action", action: "setState",
        params: { target: "appData.user", operation: "set", value: "#{_results.loginResult.data}" },
      },
    ],
  },
  {
    patterns: [/sign\s*up/i, /register/i, /create\s*account/i],
    flowName: "signUp",
    steps: [
      {
        id: "s1", type: "action", action: "restApi",
        params: { method: "POST", url: "#{appData.apiBase}/auth/register", body: "#{event.formData}" },
        resultTarget: "_results.signUpResult",
      },
    ],
  },
  {
    patterns: [/log\s*out/i, /sign\s*out/i],
    flowName: "logout",
    steps: [
      { id: "s1", type: "action", action: "setState", params: { target: "appData.user", operation: "set", value: null } },
      { id: "s2", type: "action", action: "navigate", params: { route: "/" } },
    ],
  },
  {
    patterns: [/delete/i],
    flowName: "deleteItem",
    steps: [
      {
        id: "s1", type: "action", action: "restApi",
        params: { method: "DELETE", url: "#{appData.apiBase}/items/#{event.itemId}" },
        resultTarget: "_results.deleteResult",
      },
    ],
  },
  {
    patterns: [/save/i, /update/i],
    flowName: "saveItem",
    steps: [
      {
        id: "s1", type: "action", action: "restApi",
        params: { method: "PUT", url: "#{appData.apiBase}/items/#{event.itemId}", body: "#{event.data}" },
        resultTarget: "_results.saveResult",
      },
    ],
  },
  {
    patterns: [/close/i, /cancel/i, /dismiss/i],
    flowName: "closeModal",
    steps: [
      {
        id: "s1", type: "action", action: "setState",
        params: { target: "appData.modalOpen", operation: "set", value: false },
      },
    ],
  },
]

// ─── Types ───────────────────────────────────────────────────────

export interface UiNode {
  id?: string
  type?: string
  text?: string
  className?: string
  icon?: string
  href?: string
  placeholder?: string
  children?: UiNode[]
  [key: string]: unknown
}

export interface InteractiveElement {
  /** Element ID */
  id: string
  /** Element type (button, link, input, etc.) */
  type: string
  /** Extracted text content */
  text: string
  /** Inferred event type (tap or change) */
  eventType: "tap" | "change"
  /** Matched intent definition, or null if no match */
  intent: IntentDef | null
  /** Generated flow name */
  flowName: string
}

export const INTERACTIVE_TYPES = new Set([
  "button", "link", "input", "select", "checkbox", "switch", "radio",
])

// ─── Helpers ─────────────────────────────────────────────────────

export function walkUi(root: UiNode | undefined, visitor: (node: UiNode) => void): void {
  if (!root) return
  visitor(root)
  if (Array.isArray(root.children)) {
    for (const child of root.children) walkUi(child, visitor)
  }
}

export function getElementText(node: UiNode): string {
  let text = node.text ?? ""
  if (!text && Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child.type === "text" && child.text) {
        text += " " + child.text
      }
    }
  }
  return text.trim()
}

export function inferIntent(text: string): IntentDef | null {
  for (const intent of INTENT_MAP) {
    for (const pattern of intent.patterns) {
      if (pattern.test(text)) return intent
    }
  }
  return null
}

/**
 * Per-element flow name, derived from the element id.
 *
 * The id is unique within a block by construction, so this is the only
 * naming channel here that cannot collide. Used whenever the element's text
 * yields no usable identifier.
 */
export function idFlowName(id: string): string {
  return `handle${id.replace(/[^a-zA-Z0-9]/g, "_")}Tap`
}

/**
 * Derive a flow name from an element's visible text.
 *
 * `fallbackId` — REQUIRED for any caller that names more than one element.
 * Text is not a unique key: every text that survives punctuation-stripping as
 * the empty string (icon-only glyph labels such as `-`, `+`, `x`, `→`) maps to
 * the same name. Without a fallback id, a cart's `qty-minus-0 / qty-plus-0 /
 * qty-minus-1 / …` all collapse onto one shared flow, and — because the fixer
 * only writes a flow body the first time it sees the name — every one of those
 * buttons then executes the body synthesized for whichever element happened to
 * be walked first. Measured on `pet-supply-store-new3`: 6 distinct quantity
 * buttons in `cart-view` bound to a single `handleTap` whose body was built
 * from the `-` button. Pass the element id and each element keeps its own flow.
 *
 * The 1-argument form is retained for callers naming a single element.
 *
 * `fallbackId` is NOT sufficient on its own: it is consulted only when the
 * text strips to empty, so `Add` / `add` / `ADD`, and two cards both labelled
 * "View", still return one shared name. Callers naming a whole block must go
 * through `extractInteractiveIntents`, which enforces uniqueness across the
 * assigned set (see uniqueFlowName) rather than trusting any single channel.
 */
export function toFlowName(text: string, fallbackId?: string): string {
  const cleaned = text.replace(/[^a-zA-Z0-9\s]/g, "").trim()
  if (!cleaned) return fallbackId ? idFlowName(fallbackId) : "handleTap"
  const words = cleaned.split(/\s+/)
  return "on" + words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join("")
}

// ─── Main Extractor ──────────────────────────────────────────────

/**
 * Make `preferred` unique against the names already handed out in this walk.
 *
 * NONE of the three naming channels is injective, so uniqueness cannot be a
 * property of any single name — only of the assigned SET:
 *
 *   - text → `toFlowName` upper-cases the first letter and lower-cases the
 *     rest of every word after stripping punctuation, so `Add` / `add` / `ADD`
 *     / `Add!` all yield `onAdd`. Two product cards each labelled "View" are
 *     the same collision without any case difference at all.
 *   - intent → `INTENT_MAP` maps a whole pattern family onto one constant, so
 *     "Add to Cart" and "Add item" both yield `addToCart`.
 *   - id → `idFlowName` folds every non-alphanumeric character to `_`, so the
 *     ids `qty-1` and `qty_1` both yield `handleqty_1Tap`.
 *
 * Collisions matter because `applyInteractiveFixes` writes a flow BODY only on
 * the first sight of a name and then binds every later element to that same
 * name — so the second control silently executes the first control's body.
 *
 * Resolution order is deliberate:
 *   1. the preferred name, when free — this is why single-control blocks keep
 *      the readable `addToCart` / `onSubmit` names that block specs pin;
 *   2. the preferred name suffixed with the element id — stable under tree
 *      reordering, unlike an ordinal, so re-walking a block does not churn
 *      bindings;
 *   3. an ordinal, for the residual case where two ids sanitize to the same
 *      string (`a-b` and `a_b`) AND share a preferred name.
 */
function uniqueFlowName(preferred: string, id: string, taken: Set<string>): string {
  if (!taken.has(preferred)) {
    taken.add(preferred)
    return preferred
  }
  const suffixed = `${preferred}_${id.replace(/[^a-zA-Z0-9]/g, "_")}`
  if (!taken.has(suffixed)) {
    taken.add(suffixed)
    return suffixed
  }
  let n = 2
  while (taken.has(`${suffixed}_${n}`)) n++
  const numbered = `${suffixed}_${n}`
  taken.add(numbered)
  return numbered
}

/**
 * Extract all interactive elements from a UI tree with their inferred intents.
 *
 * Returns a list of InteractiveElement objects, each with:
 *   - element ID and type
 *   - extracted text
 *   - inferred event type (tap/change)
 *   - matched intent (if any)
 *   - generated flow name — UNIQUE across the returned list (see uniqueFlowName)
 */
export function extractInteractiveIntents(uiRoot: UiNode | undefined): InteractiveElement[] {
  const elements: InteractiveElement[] = []
  const taken = new Set<string>()

  walkUi(uiRoot, (node) => {
    const id = node.id
    if (!id) return
    if (!INTERACTIVE_TYPES.has(node.type ?? "")) return

    const text = getElementText(node)
    const intent = inferIntent(text)
    const eventType = node.type === "input" || node.type === "select" ? "change" : "tap"

    let preferred: string
    if (intent) {
      preferred = intent.flowName
    } else if (text) {
      // Pass `id`: text-derived names are not unique (see toFlowName).
      preferred = toFlowName(text, id)
    } else {
      preferred = idFlowName(id)
    }

    // Text, intent and id are all non-injective — uniqueness is enforced on
    // the assigned set, not assumed from the channel. See uniqueFlowName.
    const flowName = uniqueFlowName(preferred, id, taken)

    elements.push({ id, type: node.type!, text, eventType, intent, flowName })
  })

  return elements
}
